import { Component, Input, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormErrorComponent } from '../form-error/form-error.component';

/**
 * IsPasswordFieldComponent
 *
 * Campo de senha reutilizável com toggle de visibilidade,
 * ícone prefixo e validação integrada.
 *
 * @example
 * <is-password-field
 *   label="Senha"
 *   placeholder="Digite sua senha"
 *   formControlName="password"
 *   [control]="form.get('password')"
 *   [required]="true"
 * ></is-password-field>
 */
@Component({
  selector: 'is-password-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FormErrorComponent],
  templateUrl: './password-field.component.html',
  styleUrls: ['./password-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordFieldComponent),
      multi: true,
    },
  ],
})
export class PasswordFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() helpText = '';
  @Input() required = false;
  @Input() prefixIcon = 'bx-lock-alt';
  @Input() control: AbstractControl | null = null;
  @Input() inputId = '';

  value = '';
  isDisabled = false;
  showPassword = false;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};
  private controlSub: Subscription | null = null;

  ngOnInit(): void {
    this.syncFromControl();
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
  }

  /**
   * Sincroniza o valor interno com o AbstractControl passado via @Input().
   * Assina valueChanges para reagir a patchValue/setValue/reset externos.
   */
  private syncFromControl(): void {
    if (!this.control) return;

    // Sincroniza valor inicial do FormControl
    this.value = this.control.value ?? '';

    // Observa mudanças externas no FormControl
    this.controlSub = this.control.valueChanges.subscribe((val) => {
      this.value = val ?? '';
    });
  }

  get inputType(): string {
    return this.showPassword ? 'text' : 'password';
  }

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  toggleVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  writeValue(val: string): void {
    this.value = val ?? '';
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  handleInput(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.value = val;
    this.onChange(val);
  }

  handleBlur(): void {
    this.onTouched();
  }
}
